// « Clients débiteurs » — the debtor accounts with a balance
// (PMS-TERMINAL-LINK-BRIEF §8.8l, operator 2026-09-26).
//
// A guest who leaves without paying at the desk has the note put on a debtor
// account in Vega (« Transfert débiteur », GDETREG CLDI 998): the note reads
// settled, and the money is owed by the account. A recovery (RECD — a
// transfer received, or the debt closed « impayé ») reduces the balance. One
// row per account whose balance is not zero, with what happened since it last
// stood at zero: the notes put on it — their booking and party when Vega
// still knows them — and the recoveries so far, oldest debt first.
//
// Interim source: the Vega mirror. At go-live the PMS's own receivables replace
// it; the list does not change.

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { DebtorAccount, DepartureBriefing } from '@lemontaigu/pms-core';
import { fmtAmount, fmtDate } from './logic.js';
import { SheetTop } from './SheetTop.js';
import { dayMonth, displayRoom, nightsBetween, todayParis, vegaReadLabel } from './departureSheet.js';
import { EtablissementTag } from './EtablissementTag.js';

export interface DebtorsProps {
  briefing: DepartureBriefing;
  /** The view chips, placed at the top by the Encaissement screen (§8.8k). */
  nav?: ReactNode;
  /** The Encaissement form, placed under the sheet's title. */
  form?: ReactNode;
  /** Injected for tests; the wall clock otherwise. */
  now?: Date;
}

const accountLabel = (a: string | null) => (a === 'GRP' ? 'groupe' : a ? `ch. ${displayRoom(a)}` : null);

export function Debtors({ briefing, nav, form, now }: DebtorsProps) {
  const [rows, setRows] = useState<DebtorAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await briefing.debtors();
    setLoading(false);
    if (!r.ok) {
      setError(r.error.message ?? 'lecture impossible');
      setRows([]);
      return;
    }
    setRows(r.value);
  }, [briefing]);

  useEffect(() => {
    void load();
  }, [load]);

  const clock = now ?? new Date();
  const today = todayParis(clock);
  const read = vegaReadLabel(rows.find((a) => a.readAt)?.readAt ?? null, clock);
  const total = rows.reduce((t, a) => t + a.balanceCents, 0);

  return (
    <section className="pms-print" aria-label="Clients débiteurs">
      <SheetTop nav={nav} onRefresh={() => void load()} canPrint={rows.length > 0} />
      <div className="pms-head">
        <div>
          <h2>Clients débiteurs</h2>
          <p className="pms-hint">
            {read && (
              <span className={read.stale ? 'pms-stale' : undefined}>
                {read.stale ? '⚠ ' : ''}
                {read.text}
                {' · '}
              </span>
            )}
            Notes mises en compte débiteur dans Vega (transfert débiteur), moins les recouvrements · un compte par
            ligne, la dette la plus ancienne d’abord.
          </p>
        </div>
      </div>

      {form && <div className="pms-no-print pms-sheet-form">{form}</div>}
      {error && <p className="pms-warn">⚠ {error}</p>}
      {loading && <p className="pms-hint pms-no-print">Chargement…</p>}

      <div className="pms-table-wrap">
        <table className="pms-table" aria-label="Comptes débiteurs">
          <thead>
            <tr>
              <th>Compte</th>
              <th>Depuis</th>
              <th>Notes mises en compte</th>
              <th>Recouvrements</th>
              <th>Solde dû</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const transfers = a.movements.filter((m) => m.kind === 'transfer');
              const recoveries = a.movements.filter((m) => m.kind === 'recovery');
              const days = a.openedOn ? nightsBetween(a.openedOn, today) : null;
              return (
                <tr key={a.clientRef}>
                  <td>
                    {a.name}
                    <span className="pms-sub"> ({a.clientRef})</span>
                    <EtablissementTag code={a.propertyCode} />
                  </td>
                  <td>
                    {a.openedOn ? fmtDate(a.openedOn) : '—'}
                    {days !== null && (
                      <span className="pms-sub">
                        {' '}
                        · {days} j
                      </span>
                    )}
                  </td>
                  <td>
                    <ul className="pms-pay-list">
                      {transfers.map((m, i) => (
                        <li key={`${m.facture}-${i}`}>
                          {dayMonth(m.date)} · note {m.facture ?? '?'} · {fmtAmount(m.cents)}
                          {(m.bookingRef || m.partyLabel) && (
                            <span className="pms-sub">
                              {' '}
                              · {[m.partyLabel, m.bookingRef && `résa ${m.bookingRef}`, accountLabel(m.account)]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    {recoveries.length === 0 ? (
                      '—'
                    ) : (
                      <ul className="pms-pay-list">
                        {recoveries.map((m, i) => (
                          <li key={`${m.facture}-${i}`}>
                            {dayMonth(m.date)} · {m.label ?? '?'} · {fmtAmount(m.cents)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="pms-num">
                    <strong>{fmtAmount(a.balanceCents)}</strong>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && !error && (
              <tr>
                <td colSpan={5} className="pms-empty">
                  Aucun client débiteur.
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={4} className="pms-sub">
                  {rows.length} compte{rows.length > 1 ? 's' : ''} débiteur{rows.length > 1 ? 's' : ''}
                </td>
                <td className="pms-num">{fmtAmount(total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}
